<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from 'src/services/i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Helpers
	import { formatSnakeCaseToTitle } from 'src/helpers/utilities';
	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_gen_2_component_channel_structure,
	} from 'src/helpers/interface_helpers';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from 'src/helpers/timer_helpers';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	//Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Design system components
	import { Button, Checkbox, toast, Container, TimeWheel } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';
	import type { Device } from 'src/types/Device';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;

	const id = real_id(device.id);
	const component = isPeripheralDevice(device.type)
		? device.component
		: get_gen_2_component_channel_structure(device);

	const componentIndex = isPeripheralDevice(device.type)
		? Number(device.component.split(':')[1])
		: get_device_component_index(device);

	let settingsPerChannel = $settingsStore[component],
		subsettings = ['auto_on', 'auto_off'];

	let loading = { auto_on: false, auto_off: false };
	let min = 0,
		max = 2147483;

	let timersValues = {
		auto_on: settingsPerChannel['auto_on'],
		auto_on_delay: settingsPerChannel['auto_on_delay'],
		auto_off: settingsPerChannel['auto_off'],
		auto_off_delay: settingsPerChannel['auto_off_delay'],
	};

	let selectedHour = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMinute = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedSecond = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMs = { auto_on_delay: undefined, auto_off_delay: undefined };
	let maxTimes = convertSeconds(max);

	const method = `${get_gen_2_component_from_category(device)}.SetConfig`;

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: checkBoxInfoText = {
		auto_on: `${$format('auto_on_description')} 
					<span class='text-primary text-bold'>
						${formatTimeLabel(
									selectedHour['auto_on_delay'],
									selectedMinute['auto_on_delay'],
									selectedSecond['auto_on_delay'],
									selectedMs['auto_on_delay'],
								)}
					</span>`,
		auto_off: `${$format('auto_off_description')}
					<span class='text-primary text-bold'>
						${formatTimeLabel(
									selectedHour['auto_off_delay'],
									selectedMinute['auto_off_delay'],
									selectedSecond['auto_off_delay'],
									selectedMs['auto_off_delay'],
								)}
					</span>`,
	};

	$: errors = {
		auto_on_error:
			convertToSeconds(
				selectedHour['auto_on_delay'],
				selectedMinute['auto_on_delay'],
				selectedSecond['auto_on_delay'],
				selectedMs['auto_on_delay'],
			) > max
				? infoText
				: '',
		auto_off_error:
			convertToSeconds(
				selectedHour['auto_off_delay'],
				selectedMinute['auto_off_delay'],
				selectedSecond['auto_off_delay'],
				selectedMs['auto_off_delay'],
			) > max
				? infoText
				: '',
	};

	onMount(() => {
		updateTimeValues('auto_on_delay');
		updateTimeValues('auto_off_delay');
	});

	const getTimeInSeconds = (type: 'auto_on_delay' | 'auto_off_delay') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};

	const updateTimeValues = (type: 'auto_on_delay' | 'auto_off_delay') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(timersValues[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const set_auto = async (setting) => {
		loading[setting] = true;

		const secconds = getTimeInSeconds(`${setting}_delay` as any);
		const params = {
			id: componentIndex,
			config: {
				[setting]: timersValues[setting],
				[setting + '_delay']: secconds,
			},
		};

		const res: SettingRequest = await gen2Request(device, method, params);
		if (res.isok) {
			if (!timersValues[setting]) {
				toast.open({
					type: 'success',
					title: $format('auto_turn_successfully_disable', {
						values: { auto_turn_state: $format(setting) },
					}),
				});
			} else {
				toast.open({
					type: 'success',
					title: `${$format(`${setting}_set_toast`)} ${formatTimeLabel(
						selectedHour[`${setting}_delay`],
						selectedMinute[`${setting}_delay`],
						selectedSecond[`${setting}_delay`],
						selectedMs[`${setting}_delay`],
					)}.`,
				});
			}
		} else {
			if (res?.errors?.message) {
				toast.open({ type: 'danger', title: res?.errors?.message });
			} else {
				toast.open({ type: 'danger', title: $format('timer_err_toast') });
			}
		}
		loading[setting] = false;
	};

	const handle_toggler = async (setting) => {
		timersValues[setting] = !timersValues[setting];
	};
</script>

{#each subsettings as setting}
	<Container
		backgroundColor={$showNewLayout ? 'color-0' : undefined}
		type={!$showNewLayout ? 'modalContainer' : undefined}
		padding="xs"
		class="mb-s border-color-transparent"
	>
		<Checkbox
			class="mb-s"
			size="m"
			info={checkBoxInfoText[setting]}
			checked={timersValues[setting]}
			on:click={() => handle_toggler(setting)}
			id="checkbox-fn7i75h"
		>
			{formatSnakeCaseToTitle($format(setting))}
		</Checkbox>

		<TimeWheel
			id="timewheel-cwwd3j1"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', {
					default: 'Time wheel input',
				}),
				buttonAriaLabel: $format('label_time_wheel_button', {
					default: 'Time wheel button',
				}),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[`${setting}_delay`]}
			bind:selectedMinute={selectedMinute[`${setting}_delay`]}
			bind:selectedSecond={selectedSecond[`${setting}_delay`]}
			bind:selectedMs={selectedMs[`${setting}_delay`]}
			maxHours={maxTimes.hours}
			msToggleText={getTimeWheelToggleText()}
			info={infoText}
			error={errors[`${setting}_error`]}
			texts={getTimeWheelTexts()}
			msStep={getTimeWheelMsStep(device.type)}
		/>

		<Button
			loading={loading[setting]}
			disabled={loading[setting] || errors[`${setting}_error`]}
			size="m"
			width="full"
			on:click={() => set_auto(setting)}>{$format('button_save')}</Button
		>
	</Container>
{/each}
